/**
 * WHAT: MySQL user access report with host-aware filtering and privilege drilldown modals.
 * WHY:  MySQL grants are scoped to username@host pairs and include MySQL-specific attributes like
 *       SUPER privilege and global privilege counts; this report surfaces those details for
 *       compliance review and attestation preparation.
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import API_CONFIG from '../config/api.config';
import { useSafeTimeout } from '../hooks';
import { WideModal } from './common';
import './MySQLUserAccessReport.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || API_CONFIG.getEndpoint('/api/databases');

/**
 * WHAT: Renders a filterable, paginated report of MySQL users with risk levels and privilege breakdowns.
 * WHY:  Allows MySQL DBAs and reviewers to assess user access by database, collection date,
 *       risk level, and MySQL-specific criteria (host, SUPER privilege) for attestation evidence.
 */
const MySQLUserAccessReport = () => {
  const [databases, setDatabases] = useState([]);
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [privilegeDetail, setPrivilegeDetail] = useState(null); // For privilege drilldown modal
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [collectionDates, setCollectionDates] = useState([]);

  // Pagination — server-side. The backend returns total_count/total_pages
  // for the current filter set; the client only tracks current page + size
  // and re-fetches on change.
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filters
  const [filters, setFilters] = useState({
    database_id: '',
    collection_date: '',
    risk_level: '',
    account_status: '',
    username: '',
    privilege_level: '',
    user_type: '', // Internal | Individual | Application (blank = all)
  });

  // Active privilege tiers for the matrix-driven dropdown. Disabling a tier
  // for MySQL in the privilege matrix removes it from this list.
  const [activeTiers, setActiveTiers] = useState([]);

  // Privilege Mappings certification status for MySQL (drives the warning banner
  // when there are still uncertified / unassigned mappings — derived levels in
  // the report can be understated until those are classified by a DBA).
  const [certStatus, setCertStatus] = useState(null);

  // Privilege-level breakdown drill-down. Clicking a user's Privilege Level
  // opens a WideModal showing the components (every direct grant: global /
  // schema / table / column / routine) that produced the level via MAX, then
  // matrix-clamp — same rules MySQLUserCategorizer.categorize_user applies, so
  // the breakdown agrees with the stored level shown in the row.
  const [levelBreakdown, setLevelBreakdown] = useState(null);

  // Debounce timer for username filter
  const usernameDebounceTimer = useRef(null);

  // Safe timeout hook for delayed operations
  const { setSafeTimeout } = useSafeTimeout();

  // Fetch databases on mount.
  // NOTE: do NOT call a separate summary fetch here. The stat-card `summary`
  // object is returned by the report endpoint and set by fetchUsers(); the old
  // GET mysql-access/summary/ endpoint returns {database_summary,
  // common_privileges} with NO `summary` key, so calling it set summary={} and
  // — racing with fetchUsers — clobbered the good values, making every card
  // read 0.
  useEffect(() => {
    fetchDatabases();
    fetchUsers();
    fetchActiveTiers();
    fetchCertificationStatus();
  }, []);

  // Reuses the shared check-certification endpoint so this banner is consistent
  // with the Manage Privilege Mappings page. Best-effort: a failure just hides
  // the warning, never blocks the report.
  const fetchCertificationStatus = async () => {
    try {
      const response = await axios.get(
        API_CONFIG.getDatabasesEndpoint('privilege-reference/check-certification/'),
        { params: { database_type: 'MYSQL' } }
      );
      if (response.data?.status === 'success') {
        setCertStatus(response.data);
      }
    } catch (e) {
      setCertStatus(null);
    }
  };

  // Fetch and open the privilege-level breakdown for one MySQL user. Scoped
  // by user@host + source DB so the backend resolves the exact row this
  // report rendered (MySQL users can recur across hosts and databases).
  const openLevelBreakdown = async (user) => {
    setLevelBreakdown({ name: user.username, host: user.user_host, loading: true, data: null });
    try {
      const resp = await axios.get(
        API_CONFIG.getDatabasesEndpoint('privilege-reference/mysql-user-component-levels/'),
        { params: {
          database_type: 'MYSQL',
          username: user.username,
          user_host: user.user_host || undefined,
          database_id: user.database_id || undefined,
        } }
      );
      setLevelBreakdown({ name: user.username, host: user.user_host, loading: false, data: resp.data });
    } catch (e) {
      setLevelBreakdown({
        name: user.username, host: user.user_host, loading: false,
        data: null, error: e.response?.data?.message || e.message,
      });
    }
  };

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (usernameDebounceTimer.current) {
        clearTimeout(usernameDebounceTimer.current);
      }
    };
  }, []);

  const fetchDatabases = async () => {
    try {
      const response = await axios.get(API_CONFIG.getDatabasesEndpoint('mysql-access/databases/'));
      setDatabases(response.data.databases || []);
    } catch (err) {
      console.error('Error fetching databases:', err);
    }
  };

  const fetchActiveTiers = async () => {
    try {
      const response = await axios.get(API_CONFIG.getDatabasesEndpoint('privilege-matrix/mysql/active-tiers/'));
      if (response.data?.success) {
        setActiveTiers(response.data.tiers || []);
      }
    } catch (err) {
      console.error('Failed to fetch active tiers for MySQL:', err);
    }
  };

  const fetchCollectionDates = async (databaseId = '') => {
    try {
      const params = databaseId ? `?database_id=${databaseId}` : '';
      const response = await axios.get(API_CONFIG.getDatabasesEndpoint(`mysql-access/collection-dates/${params}`));
      setCollectionDates(response.data.collection_dates || []);
    } catch (err) {
      console.error('Error fetching collection dates:', err);
      setCollectionDates([]);
    }
  };

  const fetchUsers = async (overrideFilters = null, overridePage = null, overrideSize = null) => {
    setLoading(true);
    setError(null);
    try {
      const f = overrideFilters || filters;
      const p = overridePage ?? currentPage;
      const s = overrideSize ?? pageSize;
      const payload = { ...f, page: p, page_size: s };
      const response = await axios.post(API_CONFIG.getDatabasesEndpoint('mysql-access/report/'), payload);
      setUsers(response.data.users || []);
      setSummary(response.data.summary || {});
      setTotalCount(response.data.total_count || 0);
      setTotalPages(response.data.total_pages || 0);
    } catch (err) {
      // Clear the report so a failed load (e.g. an uncollected/removed DB) shows
      // an empty report — NOT the previously-selected database's stale rows.
      setUsers([]);
      setSummary({});
      setTotalCount(0);
      setTotalPages(0);
      setError(err.response?.data?.error || 'Failed to fetch user access report');
      console.error('Error fetching MySQL users:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (username, userHost = '') => {
    setLoading(true);
    try {
      let params = filters.database_id ? `?database_id=${filters.database_id}` : '?';
      if (userHost) {
        params += `${params === '?' ? '' : '&'}user_host=${encodeURIComponent(userHost)}`;
      }
      const response = await axios.get(API_CONFIG.getDatabasesEndpoint(`mysql-access/user/${username}${params}`));

      // Parse JSON strings into objects/arrays
      const user = response.data.user;
      if (user.global_privileges && typeof user.global_privileges === 'string') {
        user.global_privileges = JSON.parse(user.global_privileges);
      }
      if (user.database_privileges && typeof user.database_privileges === 'string') {
        user.database_privileges = JSON.parse(user.database_privileges);
      }
      if (user.table_privileges && typeof user.table_privileges === 'string') {
        user.table_privileges = JSON.parse(user.table_privileges);
      }
      if (user.column_privileges && typeof user.column_privileges === 'string') {
        user.column_privileges = JSON.parse(user.column_privileges);
      }
      if (user.routine_privileges && typeof user.routine_privileges === 'string') {
        user.routine_privileges = JSON.parse(user.routine_privileges);
      }
      if (user.proxy_privileges && typeof user.proxy_privileges === 'string') {
        user.proxy_privileges = JSON.parse(user.proxy_privileges);
      }
      if (user.roles_granted && typeof user.roles_granted === 'string') {
        user.roles_granted = JSON.parse(user.roles_granted);
      }
      if (user.stats && typeof user.stats === 'string') {
        user.stats = JSON.parse(user.stats);
      }

      console.log('Parsed user details:', user);
      setSelectedUser(user);
    } catch (err) {
      console.error('Error fetching user details:', err);
      setError('Failed to fetch user details');
    } finally {
      setLoading(false);
    }
  };

  // Server-side pagination: render whatever the server returned for this page.
  // startIndex/endIndex are display-only values for "Showing X-Y of N" copy.
  const currentUsers = users;
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + users.length, totalCount);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      setCurrentPage(newPage);
      fetchUsers(filters, newPage, pageSize);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPageSize(newSize);
    setCurrentPage(1);
    fetchUsers(filters, 1, newSize);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const newFilters = { ...filters, [name]: value };
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filter changes

    // When database changes, re-fetch collection dates and reset collection_date
    if (name === 'database_id') {
      newFilters.collection_date = '';
      setFilters(newFilters);
      fetchCollectionDates(value);
    }

    // Handle username field with debouncing for dynamic filtering
    if (name === 'username') {
      // Clear existing timer
      if (usernameDebounceTimer.current) {
        clearTimeout(usernameDebounceTimer.current);
      }

      // Set new timer to trigger filter after 500ms delay
      usernameDebounceTimer.current = setSafeTimeout(() => {
        fetchUsersWithFilters(newFilters);
      }, 500);
    } else {
      // Auto-apply filters immediately for dropdown changes.
      // fetchUsersWithFilters() already refreshes the stat-card `summary` from
      // the report endpoint, so no separate summary fetch is needed (and the
      // old one returned the wrong shape — see the mount effect note).
      setSafeTimeout(() => {
        fetchUsersWithFilters(newFilters);
      }, 100);
    }
  };

  const fetchUsersWithFilters = async (filtersToUse = filters) => {
    setLoading(true);
    setError(null);
    try {
      // Filter changes always reset to page 1 — otherwise an old `page=3`
      // can land on an empty page when the new filter has fewer matches.
      const payload = { ...filtersToUse, page: 1, page_size: pageSize };
      const response = await axios.post(API_CONFIG.getDatabasesEndpoint('mysql-access/report/'), payload);
      setUsers(response.data.users || []);
      setSummary(response.data.summary || {});
      setTotalCount(response.data.total_count || 0);
      setTotalPages(response.data.total_pages || 0);
      setCurrentPage(1);
    } catch (err) {
      setError('Failed to fetch user access report');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    // fetchUsers() refreshes the stat-card summary from the report endpoint.
    fetchUsers();
  };

  const resetFilters = () => {
    const defaultFilters = {
      database_id: '',
      collection_date: '',
      risk_level: '',
      account_status: '',
      username: '',
      privilege_level: '',
      user_type: '',
    };
    setFilters(defaultFilters);
    setCollectionDates([]);
    setCurrentPage(1); // Reset to first page
    setSafeTimeout(() => {
      fetchUsersWithFilters(defaultFilters);
    }, 100);
  };

  const exportToCSV = async () => {
    try {
      const response = await axios.post(
        API_CONFIG.getDatabasesEndpoint('mysql-access/export/csv/'),
        filters,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mysql_access_report_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error exporting CSV:', err);
      setError('Failed to export CSV');
    }
  };

  const exportToPDF = async () => {
    try {
      const response = await axios.post(
        API_CONFIG.getDatabasesEndpoint('mysql-access/export/pdf/'),
        filters,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mysql_access_report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error exporting PDF:', err);
      setError('Failed to export PDF');
    }
  };

  const formatStatLabel = (key) => {
    // Convert snake_case to Title Case with proper formatting
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getRiskBadgeClass = (riskLevel) => {
    switch (riskLevel) {
      case 'CRITICAL': return 'text-danger fw-bold';
      case 'HIGH': return 'text-warning fw-bold';
      case 'MEDIUM': return 'text-info';
      case 'LOW': return 'text-secondary';
      default: return 'text-muted';
    }
  };

  const getStatusBadgeClass = (status) => {
    if (status === 'OPEN') return 'text-success';
    if (status.includes('LOCKED')) return 'text-danger';
    if (status.includes('EXPIRED')) return 'text-warning';
    return 'text-muted';
  };

  const getRiskExplanation = (user) => {
    // Parse stats if it's a string
    const stats = typeof user.stats === 'string' ? JSON.parse(user.stats) : user.stats;

    const privilegeLevel = stats?.max_privilege_level_name || 'NONE';
    const accountStatus = user.account_status || 'UNKNOWN';
    const hasProxyGrant = stats?.has_proxy_grant || false;
    const lastLogin = user.last_login;

    // Calculate days since last login
    let loginStatus = 'Never logged in';
    let daysSinceLogin = null;
    if (lastLogin) {
      const loginDate = new Date(lastLogin);
      const today = new Date();
      daysSinceLogin = Math.floor((today - loginDate) / (1000 * 60 * 60 * 24));

      if (daysSinceLogin < 90) {
        loginStatus = `Last login: ${daysSinceLogin} days ago`;
      } else if (daysSinceLogin < 365) {
        loginStatus = `Last login: ${daysSinceLogin} days ago (inactive)`;
      } else {
        loginStatus = `Last login: ${daysSinceLogin} days ago (dormant)`;
      }
    }

    // Build explanation
    const factors = [];
    factors.push(`Privilege Level: ${privilegeLevel}`);
    factors.push(`Account Status: ${accountStatus}`);
    factors.push(loginStatus);
    if (hasProxyGrant) {
      factors.push('⚠️ Has Proxy Grant capability');
    }

    return factors.join('\n');
  };

  const closeUserDetails = () => {
    setSelectedUser(null);
  };

  const showPrivilegeDetail = async (username, category, count, userHost = null) => {
    if (count === 0) return; // Don't show modal for zero counts

    setLoading(true);
    try {
      let params = filters.database_id ? `?database_id=${filters.database_id}` : '?';
      if (userHost) {
        params += `${params === '?' ? '' : '&'}user_host=${encodeURIComponent(userHost)}`;
      }
      const response = await axios.get(API_CONFIG.getDatabasesEndpoint(`mysql-access/user/${username}/${params}`));

      // Parse JSON strings into objects/arrays
      const user = response.data.user;
      if (user.global_privileges && typeof user.global_privileges === 'string') {
        user.global_privileges = JSON.parse(user.global_privileges);
      }
      if (user.database_privileges && typeof user.database_privileges === 'string') {
        user.database_privileges = JSON.parse(user.database_privileges);
      }
      if (user.table_privileges && typeof user.table_privileges === 'string') {
        user.table_privileges = JSON.parse(user.table_privileges);
      }
      if (user.column_privileges && typeof user.column_privileges === 'string') {
        user.column_privileges = JSON.parse(user.column_privileges);
      }
      if (user.routine_privileges && typeof user.routine_privileges === 'string') {
        user.routine_privileges = JSON.parse(user.routine_privileges);
      }
      if (user.proxy_privileges && typeof user.proxy_privileges === 'string') {
        user.proxy_privileges = JSON.parse(user.proxy_privileges);
      }
      if (user.roles_granted && typeof user.roles_granted === 'string') {
        user.roles_granted = JSON.parse(user.roles_granted);
      }
      if (user.stats && typeof user.stats === 'string') {
        user.stats = JSON.parse(user.stats);
      }

      console.log('Privilege detail parsed:', { category, user, count });
      setPrivilegeDetail({
        username,
        category,
        user,
        count,
        grantSource: userHost // Store user_host for display in modal title
      });
    } catch (err) {
      console.error('Error fetching privilege details:', err);
      setError('Failed to fetch privilege details');
    } finally {
      setLoading(false);
    }
  };

  const closePrivilegeDetail = () => {
    setPrivilegeDetail(null);
  };

  // Shared pagination strip — rendered above AND below the table so users can
  // navigate without scrolling. `placement` toggles only the divider border.
  const renderPaginationBar = (placement) => {
    if (totalCount === 0) return null;
    const borderStyle = placement === 'top'
      ? { borderBottom: '1px solid #e5e7eb', marginBottom: '10px' }
      : { borderTop: '1px solid #e5e7eb', marginTop: '20px' };
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '15px',
        ...borderStyle,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <label style={{ fontSize: '14px', color: '#374151' }}>Rows per page:</label>
          <select
            value={pageSize}
            onChange={handlePageSizeChange}
            style={{
              padding: '5px 10px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              fontSize: '14px',
            }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span style={{ fontSize: '14px', color: '#6b7280', marginLeft: '20px' }}>
            Showing {startIndex + 1}-{endIndex} of {totalCount}
          </span>
        </div>

        <div style={{ display: 'flex', gap: '5px' }}>
          <button
            className="btn btn-secondary btn-small"
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            style={{ opacity: currentPage === 1 ? 0.5 : 1 }}
          >
            First
          </button>
          <button
            className="btn btn-secondary btn-small"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            style={{ opacity: currentPage === 1 ? 0.5 : 1 }}
          >
            Previous
          </button>
          {[...Array(totalPages)].map((_, i) => {
            const pageNum = i + 1;
            if (
              pageNum === 1 ||
              pageNum === totalPages ||
              (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
            ) {
              return (
                <button
                  key={pageNum}
                  className={`btn btn-small ${currentPage === pageNum ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => handlePageChange(pageNum)}
                  style={{
                    minWidth: '35px',
                    fontWeight: currentPage === pageNum ? 'bold' : 'normal',
                  }}
                >
                  {pageNum}
                </button>
              );
            } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
              return <span key={pageNum} style={{ padding: '0 5px' }}>...</span>;
            }
            return null;
          })}
          <button
            className="btn btn-secondary btn-small"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            style={{ opacity: currentPage === totalPages ? 0.5 : 1 }}
          >
            Next
          </button>
          <button
            className="btn btn-secondary btn-small"
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            style={{ opacity: currentPage === totalPages ? 0.5 : 1 }}
          >
            Last
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="mysql-access-report">
      <div className="report-header">
        <h2>MySQL User Access Report</h2>
        <p className="report-subtitle">Comprehensive privilege analysis and access review</p>
      </div>

      {/* Certification warning banner — mirrors Oracle / MSSQL. Shown when
          MySQL Privilege Mappings still have unassigned or uncertified
          privileges; those don't contribute to a user's computed level, so
          the cards / table below may be understated. Hidden when everything
          is assigned + certified. */}
      {certStatus && (() => {
        const unassigned = (certStatus.unassigned_count || 0);
        const uncertPriv = (certStatus.uncertified_count || 0);
        const incomplete = unassigned + uncertPriv;
        if (incomplete === 0) return null;
        return (
          <div style={{
            background: '#fff3cd',
            border: '2px solid #ffc107',
            borderRadius: '12px',
            padding: '16px 20px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <div style={{ color: '#856404', fontSize: '0.95rem', lineHeight: 1.5 }}>
              <i className="fas fa-exclamation-triangle" style={{ marginRight: '8px' }}></i>
              <strong>Privilege levels below may be understated.</strong>
              {' '}The MySQL privilege mappings are incomplete:
              {unassigned > 0 && <> <strong>{unassigned}</strong> unassigned privilege{unassigned === 1 ? '' : 's'}</>}
              {unassigned > 0 && uncertPriv > 0 ? ',' : ''}
              {uncertPriv > 0 && <> <strong>{uncertPriv}</strong> uncertified privilege{uncertPriv === 1 ? '' : 's'}</>}.
              {' '}Unassigned or uncertified mappings do not contribute to a user's
              computed privilege level until a DBA classifies and certifies them in{' '}
              <strong>Manage Privilege Mappings</strong>, so the levels shown here
              may be lower than a user's effective access.
            </div>
          </div>
        );
      })()}

      {/* Compact summary cards — scoped override so the shared .summary-card
          used by other tabs stays untouched. Cards listed severity high → low;
          tiers with zero users are hidden so the lineup reflects the data. */}
      <style>{`
        .mysql-compact-cards {
          display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 16px;
        }
        .mysql-compact-cards .summary-card {
          flex: 1 1 96px; padding: 6px 10px; border-radius: 7px;
          box-shadow: none; transition: none;
          display: flex; align-items: baseline; justify-content: space-between;
          gap: 8px;
        }
        .mysql-compact-cards .summary-card:hover { transform: none; box-shadow: none; }
        .mysql-compact-cards .summary-label {
          font-size: 10.5px; margin-bottom: 0; white-space: nowrap;
        }
        .mysql-compact-cards .summary-value { font-size: 17px; }
      `}</style>
      {(() => {
        const SEVERITY_ORDER = [
          ['SUPER_ADMIN', 'danger'],
          ['ADMIN', 'danger'],
          ['WRITE_ELEVATED', 'warning'],
          ['WRITE', 'warning'],
          ['READ_ELEVATED', 'info'],
          ['READ', 'info'],
          ['NONE', ''],
          ['UNASSIGNED', ''],
        ];
        const dist = summary.privilege_distribution || {};
        const levelCards = SEVERITY_ORDER
          .filter(([name]) => (dist[name] || 0) > 0)
          .map(([name, cls]) => ({ name, cls, count: dist[name] }));
        return (
          <div className="summary-cards mysql-compact-cards">
            <div className="summary-card">
              <div className="summary-label">Total Users</div>
              <div className="summary-value">{summary.total_users || 0}</div>
            </div>
            <div className="summary-card warning">
              <div className="summary-label">With SUPER</div>
              <div className="summary-value">{summary.users_with_super || 0}</div>
            </div>
            {levelCards.map(c => (
              <div key={c.name} className={`summary-card ${c.cls}`}>
                <div className="summary-label">{c.name.replace(/_/g, ' ')}</div>
                <div className="summary-value">{c.count}</div>
              </div>
            ))}
            <div className="summary-card danger">
              <div className="summary-label">High Risk</div>
              <div className="summary-value">{summary.high_risk_users || 0}</div>
            </div>
            <div className="summary-card info">
              <div className="summary-label">Locked</div>
              <div className="summary-value">{summary.locked_accounts || summary.locked_users || 0}</div>
            </div>
          </div>
        );
      })()}

      {/* Filters */}
      <div className="filters-section">
        <h3>Filters</h3>
        <div className="filters-grid">
          <div className="filter-group">
            <label>Database</label>
            <select name="database_id" value={filters.database_id} onChange={handleFilterChange}>
              <option value="">All Databases</option>
              {databases.map(db => (
                <option key={db.id} value={db.id}>{db.db_name} ({db.instance_name})</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Collection Date</label>
            <select name="collection_date" value={filters.collection_date} onChange={handleFilterChange}>
              <option value="">Latest</option>
              {collectionDates.map((cd, idx) => (
                <option key={idx} value={cd.value}>{cd.label}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Risk Level</label>
            <select name="risk_level" value={filters.risk_level} onChange={handleFilterChange}>
              <option value="">All Levels</option>
              <option value="CRITICAL">Critical</option>
              <option value="HIGH">High</option>
              <option value="MEDIUM">Medium</option>
              <option value="LOW">Low</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Privilege Level</label>
            <select name="privilege_level" value={filters.privilege_level} onChange={handleFilterChange}>
              <option value="">All Levels</option>
              {activeTiers.map(t => (
                <option key={t.tier_name} value={t.tier_value}>{t.tier_name}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Account Status</label>
            <select name="account_status" value={filters.account_status} onChange={handleFilterChange}>
              <option value="">All Statuses</option>
              <option value="OPEN">Open</option>
              <option value="LOCKED">Locked</option>
              <option value="EXPIRED">Expired</option>
            </select>
          </div>

          <div className="filter-group">
            <label>User Type</label>
            <select name="user_type" value={filters.user_type} onChange={handleFilterChange}>
              <option value="">All Users</option>
              <option value="Internal">Internal</option>
              <option value="Individual">Individual</option>
              <option value="Security Group">Security Group</option>
              <option value="Application Service Account">Application Service Account</option>
              <option value="Unclassified">Unclassified</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Username Search</label>
            <input
              type="text"
              name="username"
              value={filters.username}
              onChange={handleFilterChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  // Clear debounce timer and apply immediately
                  if (usernameDebounceTimer.current) {
                    clearTimeout(usernameDebounceTimer.current);
                  }
                  applyFilters();
                }
              }}
              placeholder="Type to search username..."
            />
          </div>

          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={resetFilters}>
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Export Buttons */}
      <div className="export-section">
        <button className="btn btn-export" onClick={exportToCSV}>
          📊 Export to CSV
        </button>
        <button className="btn btn-export" onClick={exportToPDF}>
          📄 Export to PDF
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* Users Table */}
      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <div className="users-table-container">
          <h3>
            User Access Details
            {totalCount > 0
              ? ` (Showing ${startIndex + 1}-${endIndex} of ${totalCount} users)`
              : ` (0 users)`
            }
          </h3>
          {renderPaginationBar('top')}
          <div className="table-wrapper">
            <table className="table table-hover mb-0 users-table">
              <thead className="table-dark">
                <tr>
                  <th>Database</th>
                  <th>Username</th>
                  <th>User Type</th>
                  <th>User Host</th>
                  <th>Status</th>
                  <th>Global Privs</th>
                  <th>DB Privs</th>
                  <th>Table Privs</th>
                  <th>Roles</th>
                  <th>SUPER</th>
                  <th>ADMIN</th>
                  <th>DDL</th>
                  <th>WRITE_ELEVATED</th>
                  <th>WRITE</th>
                  <th>READ_ELEVATED</th>
                  <th>READ</th>
                  <th>Privilege Level</th>
                  <th>Risk Level</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentUsers.map((user, index) => (
                  <tr key={index} className={user.has_super_privilege ? 'critical-row' : ''}>
                    <td>{user.db_name}</td>
                    <td className="username-cell">{user.username}</td>
                    <td>
                      {user.user_type === 'Internal' ? (
                        <span className="text-secondary" title="System-managed by the database engine"><i className="fas fa-cog fa-xs me-1"></i>Internal</span>
                      ) : user.user_type === 'Individual' ? (
                        <span className="text-success" title="Named user matched in enterprise directory"><i className="fas fa-user fa-xs me-1"></i>Individual</span>
                      ) : user.user_type === 'Security Group' ? (
                        <span className="text-warning" title="Role or group principal (not a real user)"><i className="fas fa-users fa-xs me-1"></i>Security Group</span>
                      ) : user.user_type === 'Application Service Account' ? (
                        <span className="text-info" title="Service account registered in account_owner with a designated attester"><i className="fas fa-cube fa-xs me-1"></i>App Service</span>
                      ) : user.user_type === 'Unclassified' ? (
                        <span className="text-danger" title="Not in any registry — register in database_owners or account_owner"><i className="fas fa-question-circle fa-xs me-1"></i>Unclassified</span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>{user.user_host || '%'}</td>
                    <td>
                      <span className={getStatusBadgeClass(user.account_status)}>
                        {user.account_status}
                      </span>
                    </td>
                    <td>
                      <span
                        className="privilege-link"
                        onClick={() => showPrivilegeDetail(user.username, 'global_privileges', user.global_priv_count || 0, user.user_host)}
                        style={{ cursor: (user.global_priv_count || 0) > 0 ? 'pointer' : 'default', color: (user.global_priv_count || 0) > 0 ? '#2563eb' : 'inherit', textDecoration: (user.global_priv_count || 0) > 0 ? 'underline' : 'none' }}
                      >
                        {user.global_priv_count || 0}
                      </span>
                    </td>
                    <td>
                      <span
                        className="privilege-link"
                        onClick={() => showPrivilegeDetail(user.username, 'database_privileges', user.database_priv_count || 0, user.user_host)}
                        style={{ cursor: (user.database_priv_count || 0) > 0 ? 'pointer' : 'default', color: (user.database_priv_count || 0) > 0 ? '#2563eb' : 'inherit', textDecoration: (user.database_priv_count || 0) > 0 ? 'underline' : 'none' }}
                      >
                        {user.database_priv_count || 0}
                      </span>
                    </td>
                    <td>
                      <span
                        className="privilege-link"
                        onClick={() => showPrivilegeDetail(user.username, 'table_privileges', user.table_priv_count || 0, user.user_host)}
                        style={{ cursor: (user.table_priv_count || 0) > 0 ? 'pointer' : 'default', color: (user.table_priv_count || 0) > 0 ? '#2563eb' : 'inherit', textDecoration: (user.table_priv_count || 0) > 0 ? 'underline' : 'none' }}
                      >
                        {user.table_priv_count || 0}
                      </span>
                    </td>
                    <td>
                      <span
                        className="privilege-link"
                        onClick={() => showPrivilegeDetail(user.username, 'roles', user.role_count || 0, user.user_host)}
                        style={{ cursor: (user.role_count || 0) > 0 ? 'pointer' : 'default', color: (user.role_count || 0) > 0 ? '#2563eb' : 'inherit', textDecoration: (user.role_count || 0) > 0 ? 'underline' : 'none' }}
                      >
                        {user.role_count || 0}
                      </span>
                    </td>
                    <td className={user.has_super_privilege ? 'critical-cell' : ''}>
                      <span style={{ color: user.has_super_privilege ? '#dc2626' : 'inherit', fontWeight: user.has_super_privilege ? 'bold' : 'normal' }}>
                        {user.has_super_privilege ? 'YES' : 'NO'}
                      </span>
                    </td>
                    <td className={user.has_high_risk_privileges ? 'high-risk-cell' : ''}>
                      <span style={{ color: user.has_high_risk_privileges ? '#ea580c' : 'inherit', fontWeight: user.has_high_risk_privileges ? 'bold' : 'normal' }}>
                        {user.has_high_risk_privileges ? 'YES' : 'NO'}
                      </span>
                    </td>
                    <td>
                      <span style={{ color: user.has_ddl_capability ? '#f59e0b' : 'inherit', fontWeight: user.has_ddl_capability ? 'bold' : 'normal' }}>
                        {user.has_ddl_capability ? 'YES' : 'NO'}
                      </span>
                    </td>
                    <td>
                      <span style={{ color: user.has_write_elevated_capability ? '#10b981' : 'inherit', fontWeight: user.has_write_elevated_capability ? 'bold' : 'normal' }}>
                        {user.has_write_elevated_capability ? 'YES' : 'NO'}
                      </span>
                    </td>
                    <td>
                      <span style={{ color: user.has_write_capability ? '#2563eb' : 'inherit', fontWeight: user.has_write_capability ? 'bold' : 'normal' }}>
                        {user.has_write_capability ? 'YES' : 'NO'}
                      </span>
                    </td>
                    <td>
                      <span style={{ color: user.has_read_elevated_capability ? '#6366f1' : 'inherit', fontWeight: user.has_read_elevated_capability ? 'bold' : 'normal' }}>
                        {user.has_read_elevated_capability ? 'YES' : 'NO'}
                      </span>
                    </td>
                    <td>
                      <span style={{ color: user.has_read_capability ? '#8b5cf6' : 'inherit', fontWeight: user.has_read_capability ? 'bold' : 'normal' }}>
                        {user.has_read_capability ? 'YES' : 'NO'}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        onClick={() => openLevelBreakdown(user)}
                        title="Show how this level was derived"
                        style={{
                          background: 'transparent', border: 'none', padding: 0,
                          cursor: 'pointer',
                        }}
                      >
                        <span className={`privilege-level-badge privilege-level-${(user.privilege_level_name || 'N/A').toLowerCase().replace(/_/g, '-')}`}>
                          {user.privilege_level_name || 'N/A'}
                        </span>
                      </button>
                    </td>
                    <td>
                      <span
                        className={getRiskBadgeClass(user.overall_risk_level)}
                        title={getRiskExplanation(user)}
                        style={{ cursor: 'help' }}
                      >
                        {user.overall_risk_level}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-small"
                        onClick={() => fetchUserDetails(user.username, user.user_host)}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {renderPaginationBar('bottom')}
        </div>
      )}

      {/* User Details Modal */}
      <WideModal
        show={!!selectedUser}
        onClose={closeUserDetails}
        title={
          selectedUser ? (
            <>
              <span style={{ color: '#60a5fa', fontWeight: '800' }}>{selectedUser.username}</span>
              <span style={{ color: '#d1d5db', fontWeight: '400', marginLeft: '12px' }}>- Access Details</span>
            </>
          ) : ''
        }
        headerIcon="fa-user"
        maxWidth="1100px"
        footer={<button className="btn btn-secondary" onClick={closeUserDetails}>Close</button>}
      >
        {selectedUser && (
          <>
              {/* User Info */}
              <div className="detail-section">
                <h4>User Information</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Database:</span>
                    <span className="detail-value">{selectedUser.db_name}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Account Status:</span>
                    <span className={getStatusBadgeClass(selectedUser.account_status)}>
                      {selectedUser.account_status}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Created:</span>
                    <span className="detail-value">
                      {selectedUser.created_date ? new Date(selectedUser.created_date).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Last Login:</span>
                    <span className="detail-value">
                      {selectedUser.last_login ? new Date(selectedUser.last_login).toLocaleString() : 'Never'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Statistics */}
              <div className="detail-section">
                <h4>Privilege Statistics</h4>
                <div className="stats-grid">
                  {selectedUser.stats && Object.entries(selectedUser.stats)
                    .filter(([key, value]) => typeof value !== 'object' && !Array.isArray(value))
                    .map(([key, value]) => (
                      <div key={key} className="stat-item" style={{ padding: '4px 8px', fontSize: '9px' }}>
                        <span className="stat-label" style={{ fontSize: '9px' }}>{formatStatLabel(key)}:</span>
                        <span className="stat-value" style={{ fontSize: '9px' }}>{value}</span>
                      </div>
                    ))}
                </div>
              </div>

              {/* Global Privileges */}
              {selectedUser.global_privileges && selectedUser.global_privileges.length > 0 && (
                <div className="detail-section">
                  <h4>Global Privileges ({selectedUser.global_privileges.length})</h4>
                  <div className="privilege-list">
                    {selectedUser.global_privileges.slice(0, 20).map((priv, idx) => (
                      <div key={idx} className="privilege-item">
                        <span className="priv-name">{priv}</span>
                        <span className="priv-source">GLOBAL</span>
                      </div>
                    ))}
                    {selectedUser.global_privileges.length > 20 && (
                      <div className="more-items">...and {selectedUser.global_privileges.length - 20} more</div>
                    )}
                  </div>
                </div>
              )}

              {/* Database Privileges */}
              {selectedUser.database_privileges && selectedUser.database_privileges.length > 0 && (
                <div className="detail-section">
                  <h4>Database Privileges ({selectedUser.database_privileges.length})</h4>
                  <div className="privilege-list">
                    {selectedUser.database_privileges.slice(0, 20).map((db, idx) => (
                      <div key={idx} className="privilege-item">
                        <span className="priv-name">{db.db || db.database}</span>
                        <span className="priv-type">{Array.isArray(db.privileges) ? db.privileges.join(', ') : db.privileges}</span>
                      </div>
                    ))}
                    {selectedUser.database_privileges.length > 20 && (
                      <div className="more-items">...and {selectedUser.database_privileges.length - 20} more</div>
                    )}
                  </div>
                </div>
              )}

              {/* Table Privileges */}
              {selectedUser.table_privileges && selectedUser.table_privileges.length > 0 && (
                <div className="detail-section">
                  <h4>Table Privileges ({selectedUser.table_privileges.length})</h4>
                  <div className="privilege-list">
                    {selectedUser.table_privileges.slice(0, 20).map((tbl, idx) => (
                      <div key={idx} className="privilege-item">
                        <span className="priv-name">{tbl.db}.{tbl.table || tbl.table_name}</span>
                        <span className="priv-type">{Array.isArray(tbl.privileges) ? tbl.privileges.join(', ') : tbl.privileges}</span>
                      </div>
                    ))}
                    {selectedUser.table_privileges.length > 20 && (
                      <div className="more-items">...and {selectedUser.table_privileges.length - 20} more</div>
                    )}
                  </div>
                </div>
              )}

              {/* Roles */}
              {selectedUser.roles_granted && selectedUser.roles_granted.length > 0 && (
                <div className="detail-section">
                  <h4>MySQL Roles ({selectedUser.roles_granted.length})</h4>
                  <div className="privilege-list">
                    {selectedUser.roles_granted.slice(0, 15).map((role, idx) => (
                      <div key={idx} className="privilege-item">
                        <span className="priv-name">
                          {typeof role === 'object' ? `${role.role_name}@${role.role_host}` : role}
                        </span>
                        <span className="priv-source">
                          {typeof role === 'object' && role.admin_option ? 'With Admin Option' : 'MySQL Role'}
                        </span>
                      </div>
                    ))}
                    {selectedUser.roles_granted.length > 15 && (
                      <div className="more-items">...and {selectedUser.roles_granted.length - 15} more</div>
                    )}
                  </div>
                </div>
              )}

              {/* Proxy Privileges */}
              {selectedUser.proxy_privileges && selectedUser.proxy_privileges.length > 0 && (
                <div className="detail-section">
                  <h4>Proxy Privileges ({selectedUser.proxy_privileges.length})</h4>
                  <div className="privilege-list">
                    {selectedUser.proxy_privileges.map((proxy, idx) => (
                      <div key={idx} className="privilege-item">
                        <span className="priv-name">Can proxy as: {proxy.proxied_user}@{proxy.proxied_host}</span>
                        <span className="priv-source">{proxy.with_grant === 'YES' ? 'With Grant' : 'No Grant'}</span>
                        <span className="risk-badge critical" style={{ fontSize: '10px' }}>HIGH RISK</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
          </>
        )}
      </WideModal>

      {/* Privilege Detail Modal */}
      <WideModal
        show={!!(privilegeDetail && privilegeDetail.user)}
        onClose={closePrivilegeDetail}
        title={
          privilegeDetail ? (
            <>
              {privilegeDetail.category === 'global_privileges' && 'Global Privileges'}
              {privilegeDetail.category === 'database_privileges' && 'Database-Level Privileges'}
              {privilegeDetail.category === 'table_privileges' && 'Table-Level Privileges'}
              {privilegeDetail.category === 'column_privileges' && 'Column-Level Privileges'}
              {privilegeDetail.category === 'routine_privileges' && 'Routine Privileges (Procedures/Functions)'}
              {privilegeDetail.category === 'proxy_privileges' && 'Proxy Privileges'}
              {privilegeDetail.category === 'roles' && 'MySQL Roles'}
              {' for '}{privilegeDetail.username}@{privilegeDetail.grantSource || '%'} ({privilegeDetail.count})
            </>
          ) : ''
        }
        headerIcon="fa-key"
        maxWidth="800px"
        footer={<button className="btn btn-secondary" onClick={closePrivilegeDetail}>Close</button>}
      >
        {privilegeDetail && privilegeDetail.user && (
          <>
              {/* Global Privileges */}
              {privilegeDetail.category === 'global_privileges' && privilegeDetail.user.global_privileges && (
                <div className="privilege-list">
                  {Array.isArray(privilegeDetail.user.global_privileges) && privilegeDetail.user.global_privileges.length > 0 ? (
                    privilegeDetail.user.global_privileges.map((priv, idx) => (
                      <div key={idx} className="privilege-item" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px', padding: '10px', borderBottom: '1px solid #e5e7eb' }}>
                        <span className="priv-name" style={{ fontWeight: '500' }}>{priv}</span>
                        <span className="priv-source" style={{ fontSize: '13px', color: '#6b7280' }}>GLOBAL</span>
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>No global privileges</div>
                  )}
                </div>
              )}

              {/* Database Privileges */}
              {privilegeDetail.category === 'database_privileges' && privilegeDetail.user.database_privileges && (
                <div className="privilege-list">
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr', gap: '10px', padding: '10px', backgroundColor: '#f9fafb', fontWeight: 'bold', borderBottom: '2px solid #d1d5db' }}>
                      <span>Database</span>
                      <span>Privileges</span>
                    </div>
                    {Array.isArray(privilegeDetail.user.database_privileges) && privilegeDetail.user.database_privileges.length > 0 ? (
                      privilegeDetail.user.database_privileges.map((db, idx) => (
                        <div key={idx} className="privilege-item" style={{ display: 'grid', gridTemplateColumns: '2fr 2fr', gap: '10px', padding: '10px', borderBottom: '1px solid #e5e7eb' }}>
                          <span className="priv-name" style={{ fontWeight: '500' }}>{db.db || db.database}</span>
                          <span className="priv-type" style={{ fontSize: '13px' }}>{Array.isArray(db.privileges) ? db.privileges.join(', ') : db.privileges}</span>
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>No database privileges</div>
                    )}
                  </div>
                </div>
              )}

              {/* Table Privileges */}
              {privilegeDetail.category === 'table_privileges' && privilegeDetail.user.table_privileges && (
                <div className="privilege-list">
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 2fr', gap: '10px', padding: '10px', backgroundColor: '#f9fafb', fontWeight: 'bold', borderBottom: '2px solid #d1d5db' }}>
                      <span>Database</span>
                      <span>Table</span>
                      <span>Privileges</span>
                    </div>
                    {Array.isArray(privilegeDetail.user.table_privileges) && privilegeDetail.user.table_privileges.length > 0 ? (
                      privilegeDetail.user.table_privileges.map((tbl, idx) => (
                        <div key={idx} className="privilege-item" style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 2fr', gap: '10px', padding: '10px', borderBottom: '1px solid #e5e7eb' }}>
                          <span style={{ fontSize: '13px' }}>{tbl.db || tbl.database}</span>
                          <span className="priv-name" style={{ fontWeight: '500' }}>{tbl.table || tbl.table_name}</span>
                          <span className="priv-type" style={{ fontSize: '13px' }}>{Array.isArray(tbl.privileges) ? tbl.privileges.join(', ') : tbl.privileges}</span>
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>No table privileges</div>
                    )}
                  </div>
                </div>
              )}

              {/* Column Privileges */}
              {privilegeDetail.category === 'column_privileges' && privilegeDetail.user.column_privileges && (
                <div className="privilege-list">
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 2fr', gap: '10px', padding: '10px', backgroundColor: '#f9fafb', fontWeight: 'bold', borderBottom: '2px solid #d1d5db' }}>
                      <span>Database</span>
                      <span>Table</span>
                      <span>Column</span>
                      <span>Privilege</span>
                    </div>
                    {Array.isArray(privilegeDetail.user.column_privileges) && privilegeDetail.user.column_privileges.length > 0 ? (
                      privilegeDetail.user.column_privileges.map((col, idx) => (
                        <div key={idx} className="privilege-item" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 2fr', gap: '10px', padding: '10px', borderBottom: '1px solid #e5e7eb' }}>
                          <span style={{ fontSize: '13px' }}>{col.db || col.database}</span>
                          <span style={{ fontSize: '13px' }}>{col.table || col.table_name}</span>
                          <span className="priv-name" style={{ fontWeight: '500' }}>{col.column || col.column_name}</span>
                          <span className="priv-type" style={{ fontSize: '13px' }}>{col.privilege}</span>
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>No column privileges</div>
                    )}
                  </div>
                </div>
              )}

              {/* Routine Privileges */}
              {privilegeDetail.category === 'routine_privileges' && privilegeDetail.user.routine_privileges && (
                <div className="privilege-list">
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 1fr', gap: '10px', padding: '10px', backgroundColor: '#f9fafb', fontWeight: 'bold', borderBottom: '2px solid #d1d5db' }}>
                      <span>Database</span>
                      <span>Routine</span>
                      <span>Type</span>
                      <span>Privilege</span>
                    </div>
                    {Array.isArray(privilegeDetail.user.routine_privileges) && privilegeDetail.user.routine_privileges.length > 0 ? (
                      privilegeDetail.user.routine_privileges.map((rout, idx) => (
                        <div key={idx} className="privilege-item" style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 1fr', gap: '10px', padding: '10px', borderBottom: '1px solid #e5e7eb' }}>
                          <span style={{ fontSize: '13px' }}>{rout.db || rout.database}</span>
                          <span className="priv-name" style={{ fontWeight: '500' }}>{rout.routine || rout.routine_name}</span>
                          <span style={{ fontSize: '13px' }}>{rout.type || rout.routine_type}</span>
                          <span className="priv-type" style={{ fontSize: '13px' }}>{rout.privilege}</span>
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>No routine privileges</div>
                    )}
                  </div>
                </div>
              )}

              {/* Proxy Privileges */}
              {privilegeDetail.category === 'proxy_privileges' && privilegeDetail.user.proxy_privileges && (
                <div className="privilege-list">
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr', gap: '10px', padding: '10px', backgroundColor: '#f9fafb', fontWeight: 'bold', borderBottom: '2px solid #d1d5db' }}>
                      <span>Proxied User</span>
                      <span>Proxied Host</span>
                      <span>With Grant</span>
                    </div>
                    {Array.isArray(privilegeDetail.user.proxy_privileges) && privilegeDetail.user.proxy_privileges.length > 0 ? (
                      privilegeDetail.user.proxy_privileges.map((proxy, idx) => (
                        <div key={idx} className="privilege-item" style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr', gap: '10px', padding: '10px', borderBottom: '1px solid #e5e7eb' }}>
                          <span className="priv-name" style={{ fontWeight: '500' }}>{proxy.proxied_user}</span>
                          <span style={{ fontSize: '13px' }}>{proxy.proxied_host}</span>
                          <span style={{ fontSize: '13px', color: proxy.with_grant === 'YES' ? '#dc2626' : '#6b7280' }}>{proxy.with_grant}</span>
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>No proxy privileges</div>
                    )}
                  </div>
                </div>
              )}

              {/* Roles */}
              {privilegeDetail.category === 'roles' && privilegeDetail.user.roles_granted && (
                <div className="privilege-list">
                  {Array.isArray(privilegeDetail.user.roles_granted) && privilegeDetail.user.roles_granted.length > 0 ? (
                    privilegeDetail.user.roles_granted.map((role, idx) => (
                      <div key={idx} className="privilege-item" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px', padding: '10px', borderBottom: '1px solid #e5e7eb' }}>
                        <span className="priv-name" style={{ fontWeight: '500' }}>
                          {typeof role === 'object' ? `${role.role_name}@${role.role_host}` : role}
                        </span>
                        <span className="priv-source" style={{ fontSize: '13px', color: '#6b7280' }}>
                          {typeof role === 'object' && role.admin_option ? 'With Admin Option' : 'MySQL Role'}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>No roles granted</div>
                  )}
                </div>
              )}
          </>
        )}
      </WideModal>

      {/* Privilege-level breakdown — every direct grant (global / schema /
          table / column / routine) that produced this user's level via MAX,
          then matrix-clamped. Mirrors the derivation MySQLUserCategorizer
          applies, so it agrees with the level shown in the row. */}
      <WideModal
        show={!!levelBreakdown}
        onClose={() => setLevelBreakdown(null)}
        title={`Privilege Level — ${levelBreakdown?.name || ''}${levelBreakdown?.host ? `@${levelBreakdown.host}` : ''}`}
        headerIcon="fa-calculator"
        headerClass="bg-dark text-white"
        maxWidth="900px"
        footer={<button className="btn btn-secondary" onClick={() => setLevelBreakdown(null)}>Close</button>}
      >
        {levelBreakdown?.loading && (
          <div style={{ textAlign: 'center', padding: '24px' }}>
            <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>Loading breakdown…
          </div>
        )}
        {levelBreakdown?.error && (
          <div className="error-message">{levelBreakdown.error}</div>
        )}
        {levelBreakdown?.data && (() => {
          const d = levelBreakdown.data;
          const order = { SUPER_ADMIN: 6, ADMIN: 5, WRITE_ELEVATED: 4, WRITE: 3, READ_ELEVATED: 2, READ: 1, NONE: 0, UNASSIGNED: -1 };
          // Collapse to DISTINCT (component, kind, scope, level). The same
          // privilege granted on many objects (e.g. SELECT on each table) is
          // many rows in source data but one derivation factor; only distinct
          // (privilege+level) drives the MAX. Deduped row is a driver if ANY
          // occurrence drives.
          const dedupMap = new Map();
          for (const c of (d.components || [])) {
            const key = `${c.component}|${c.kind}|${c.scope}|${c.level_name}`;
            const existing = dedupMap.get(key);
            if (existing) {
              existing.is_driver = existing.is_driver || c.is_driver;
            } else {
              dedupMap.set(key, { ...c });
            }
          }
          const rows = [...dedupMap.values()].sort((a, b) => (order[b.level_name] ?? 0) - (order[a.level_name] ?? 0));
          const stale = d.stored_level_name && d.stored_level_name !== d.derived_level_name;
          return (
            <>
              {stale && (
                <div style={{
                  background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px',
                  padding: '10px 14px', marginBottom: '14px', color: '#856404', fontSize: '0.9rem'
                }}>
                  <i className="fas fa-triangle-exclamation" style={{ marginRight: '8px' }}></i>
                  Stored level is <strong>{d.stored_level_name}</strong> but a live recompute gives{' '}
                  <strong>{d.derived_level_name}</strong>. Re-run the data refresh to reconcile.
                </div>
              )}
              <div style={{
                display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '14px',
                background: '#f8f9fa', borderRadius: '6px', padding: '10px 14px', marginBottom: '16px'
              }}>
                <span className={getRiskBadgeClass(d.derived_level_name)} style={{ fontSize: '1.05rem' }}>
                  {d.derived_level_name}
                </span>
                <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                  Level = <strong>MAX</strong> of every direct grant below
                  {d.matrix_capped
                    ? <> — raw <strong>{d.raw_max_level_name}</strong> was clamped to the matrix cap.</>
                    : <>.</>}
                </span>
                <span style={{ marginLeft: 'auto', color: '#9ca3af', fontSize: '0.8rem' }}>
                  {d.database_name || 'N/A'} · {d.component_count} grant(s)
                </span>
              </div>
              {rows.length === 0 ? (
                <p style={{ color: '#6b7280', padding: '12px' }}>
                  No direct grants contribute to this level.
                </p>
              ) : (
                <table className="table table-sm table-hover mb-0">
                  <thead className="table-dark">
                    <tr>
                      <th>Privilege</th>
                      <th>Type</th>
                      <th>Scope</th>
                      <th>Level</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((c, i) => (
                      <tr key={i} style={c.is_driver ? { background: '#fff8e1' } : undefined}>
                        <td style={{ fontWeight: 600 }}>{c.component}</td>
                        <td>{c.kind}</td>
                        <td><span style={{ color: '#6b7280', fontSize: '12px' }}>{c.scope}</span></td>
                        <td>
                          <span className={`privilege-level-badge privilege-level-${(c.level_name || 'NONE').toLowerCase().replace(/_/g, '-')}`}>
                            {c.level_name}
                          </span>
                        </td>
                        <td style={{ fontSize: '12px' }}>
                          {c.is_driver && <span style={{ color: '#d97706', fontWeight: 600 }}><i className="fas fa-bolt" style={{ marginRight: '4px' }}></i>drives level</span>}
                          {c.unknown && <span style={{ color: '#9ca3af', marginLeft: '8px' }}><i className="fas fa-question-circle" style={{ marginRight: '4px' }}></i>unknown</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          );
        })()}
      </WideModal>
    </div>
  );
};

export default MySQLUserAccessReport;
